import { FC, useCallback, useEffect, useState } from 'react'
import { MainLayout } from 'views/layouts'
import { useNavigate, Link } from 'react-router-dom'

import {
  BtnGoBack,
  ContainerActionItem,
  GridModal,
  ModalBody,
  ModalContainer,
  Row,
  SaveButton,
  SkillsDevelopmentActionsTableCard,
  SkillsDevelopmentActionsTableHeader,
  SkillsDevelopmentActionsTableTitle,
} from './styled'
import { Button, DangerButton, StrokedButton } from 'views/components/UI/Buttons'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import ActionDetailModal from './action-detail-modal/action-detail-modal'
import fetchAllActionsSkills from 'services/developmentActions/fetchAllActions/fetchAllActionsSkills'
import fetchDeleteDevelopmentPlanById from 'services/developmentActions/fetchDeleteDevelopmentPlanById/fetchDeleteDevelopmentPlanById'
import BluesiteTableComponent from 'views/components/UI/BluesiteTable'
import { HEADERS } from './constants'
import { specialAdminRoutes } from 'router/routes'
import { useNotification } from 'lib/context/notification.context'
import Modal from 'views/components/UI/Modal'

interface DevelopmentPlansProps {
  id?: number
}

const DevelopmentPlans: FC<DevelopmentPlansProps> = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [actions, setActions] = useState([])
  const [actionId, setActionId] = useState(null)
  const [openModal, setOpenModal] = useState(false)
  const [deleteActionId, setDeleteActionId] = useState<number | null>(null)
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [totalPages, setTotalPages] = useState(0)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const navigate = useNavigate()
  const { getError } = useNotification()

  const getAllDevelopmentActions = useCallback(
    (page = 1, limit = 10) => {
      setCurrentPage(page)
      fetchAllActionsSkills(limit, page, { search: searchTerm }).then(({ development_plans, total_pages }) => {
        setActions(development_plans)
        setTotalPages(total_pages)
      })
    },
    [searchTerm]
  )

  const handleDeleteButton = () => {
    if (!deleteActionId) {
      getError('No se pudo obtener el id de la acción de desarrollo')
      return
    }

    fetchDeleteDevelopmentPlanById(deleteActionId).then((response) => {
      if (response?.ok === false || response?.status === 'error') {
        getError(response.message || 'No se pudo eliminar la acción de desarrollo')
        return
      }

      setOpenDeleteModal(false)
      setDeleteActionId(null)
      getAllDevelopmentActions(currentPage)
    })
  }

  useEffect(() => {
    getAllDevelopmentActions()
  }, [getAllDevelopmentActions])

  const ActionComponent = useCallback(
    (props: any) => {
      const { id } = props

      const setActionAndOpenModal = () => {
        if (id) {
          setActionId(id)
          setOpenModal(true)
        } else {
          getError('No se pudo obtener el id de la acción de desarrollo')
        }
      }

      const setActionAndOpenDeleteModal = () => {
        if (id) {
          setDeleteActionId(id)
          setOpenDeleteModal(true)
        } else {
          getError('No se pudo obtener el id de la acción de desarrollo')
        }
      }

      return (
        <ContainerActionItem>
          <StrokedButton
            size="sm"
            leadingIcon={<VisibilityOutlinedIcon sx={{ fontSize: 18 }} />}
            onClick={setActionAndOpenModal}
          >
            Abrir
          </StrokedButton>
          <Link
            to={id ? `/modules/skills/actions/update?id=${id}` : '#'}
            style={{ display: 'inline-flex', textDecoration: 'none' }}
            onPointerDown={(event) => {
              if (event.button !== 0 || !id) return
              event.preventDefault()
              navigate(`/modules/skills/actions/update?id=${id}`)
            }}
          >
            <StrokedButton size="sm" leadingIcon={<EditOutlinedIcon sx={{ fontSize: 18 }} />}>
              Editar
            </StrokedButton>
          </Link>
          <DangerButton
            size="sm"
            leadingIcon={<DeleteOutlineOutlinedIcon sx={{ fontSize: 18 }} />}
            onClick={setActionAndOpenDeleteModal}
          >
            Eliminar
          </DangerButton>
        </ContainerActionItem>
      )
    },
    [getError, navigate]
  )

  return (
    <MainLayout>
      <SkillsDevelopmentActionsTableCard>
        <SkillsDevelopmentActionsTableHeader>
          <SkillsDevelopmentActionsTableTitle>
            Acciones de desarrollo por competencia
          </SkillsDevelopmentActionsTableTitle>
          <Button type="button" onClick={() => navigate(specialAdminRoutes.NewActionSkill)}>
            Nueva acción
          </Button>
        </SkillsDevelopmentActionsTableHeader>

        <BluesiteTableComponent
          data={actions}
          headers={HEADERS}
          tableStyle="skill-development-actions-management"
          searchBar={true}
          ActionComponent={ActionComponent}
          searchLabel=""
          totalPages={totalPages}
          handlePagination={getAllDevelopmentActions}
          currentPage={currentPage}
          onSearch={(value: string) => setSearchTerm(value as any)}
        />
      </SkillsDevelopmentActionsTableCard>
      {actionId && <ActionDetailModal openModal={openModal} setOpenModal={setOpenModal} actionId={actionId} />}
      <Modal
        hideCloseButton={true}
        show={openDeleteModal}
        setShow={setOpenDeleteModal}
        styleContent={{
          width: '560px',
          height: '280px',
          borderRadius: '10px',
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          backgroundColor: '#FFFFFF',
        }}
      >
        <ModalContainer>
          <ModalBody>
            <DeleteOutlineOutlinedIcon />
            <h3 style={{ textAlign: 'center' }}>¿Seguro que deseas eliminar esta acción?</h3>
            <GridModal>
              <p style={{ width: '100%', marginTop: 0, color: '#303030', textAlign: 'center' }}>
                Si eliminas esta acción no podrás volver a acceder a ella
              </p>
              <Row>
                <BtnGoBack
                  onClick={() => {
                    setOpenDeleteModal(false)
                  }}
                >
                  Cancelar
                </BtnGoBack>
                <SaveButton onClick={handleDeleteButton}>Eliminar</SaveButton>
              </Row>
            </GridModal>
          </ModalBody>
        </ModalContainer>
      </Modal>
    </MainLayout>
  )
}

export default DevelopmentPlans
